import type { Menu } from '@tauri-apps/api/menu'
import { useCallback, useEffect, useRef } from 'react'
import { createLogger } from '@/lib/logger'
import { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import { isTauri } from '@/lib/utils/tauri'

const logger = createLogger('hooks/useNativeRecordMenu')

/** One native menu resource per table; callbacks belong to the opening session. */
export function useNativeRecordMenu() {
  const generation = useRef(0)
  const resource = useRef<Menu | null>(null)
  useEffect(
    () => () => {
      generation.current++
      void resource.current
        ?.close()
        .catch(error => logger.debug('Menu cleanup:', error))
      resource.current = null
    },
    [],
  )
  return useCallback(
    async ({ x, y, onOpen }: { x: number; y: number; onOpen?: () => void }) => {
      if (!isTauri()) return
      const current = ++generation.current
      const scope = getWorkspaceSessionSnapshot()
      const run = (action: () => void) => {
        try {
          scope.assertUnchanged()
          if (generation.current === current) action()
        } catch (error) {
          logger.debug('Stale record menu dismissed:', error)
        }
      }
      try {
        const [{ Menu }, { LogicalPosition }] = await Promise.all([
          import('@tauri-apps/api/menu'),
          import('@tauri-apps/api/dpi'),
        ])
        if (generation.current !== current) return
        await resource.current?.close()
        resource.current = null
        const menu = await Menu.new({
          items: [
            ...(onOpen
              ? [
                  {
                    id: 'open-record',
                    text: 'Open record',
                    action: () => run(onOpen),
                  },
                ]
              : []),
            { item: 'Copy', text: 'Copy value' },
          ],
        })
        if (generation.current !== current) {
          await menu.close()
          return
        }
        try {
          scope.assertUnchanged()
        } catch (error) {
          await menu.close()
          throw error
        }
        resource.current = menu
        // popup resolves when shown, so retain the resource until replacement or
        // unmount rather than releasing it before a menu item can be selected.
        await menu.popup(new LogicalPosition(x, y))
      } catch (error) {
        logger.warn('Native record menu unavailable:', error)
      }
    },
    [],
  )
}
